import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";
import { navigate } from "../App";
import { ProductAnchor } from "../components/ProductAnchor";

const IMAGES: Record<string, string> = {
  cate: "/assets/screenshots/cate.png",
};

export function About() {
  const { t } = useTranslation("about");
  const { t: tc } = useTranslation("common");
  const stats = t("stats", { returnObjects: true }) as { v: string; k: string }[];
  const coreBlocks = t("coreBlocks", { returnObjects: true }) as { h: string; b: string }[];
  const principles = t("principles", { returnObjects: true }) as { n: string; title: string; body: string }[];
  const products = t("products", { returnObjects: true }) as { slug: string; name: string; desc: string }[];
  const stack = t("stack", { returnObjects: true }) as { label: string; note: string }[];
  const services = t("services", { returnObjects: true }) as { label: string; href: string; desc: string }[];
  const githubCard = t("githubCard", { returnObjects: true }) as { title: string; body: string };
  const paul = t("paul", { returnObjects: true }) as { role: string; name: string; body: string };
  const anton = t("anton", { returnObjects: true }) as { role: string; name: string; body: string };

  return (
    <SubPage title={t("title")} subtitle={t("subtitle")} heroImage="/assets/team/founders-portrait.png" heroObjectPositionMobile="70% center">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-grey-800">
        {stats.map((s) => (
          <div key={s.k} className="bg-grey-975 px-5 sm:px-8 md:px-10 py-8">
            <div className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-1">{s.v}</div>
            <div className="text-xs text-grey-500 leading-snug">{s.k}</div>
          </div>
        ))}
      </div>
      <FullDivider />

      <div className="relative">
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4">
          <span className="block mb-3" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono', monospace" }}>{t("foundersEyebrow")}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("foundersTitle")}</h2>
          <p className="text-sm text-grey-400 max-w-2xl leading-relaxed">{t("foundersBody")}</p>
        </div>
      </div>
      <FullDivider />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <div className="text-xs text-grey-500 mb-1 font-mono">{paul.role}</div>
          <h3 className="text-lg font-semibold text-white mb-3">{paul.name}</h3>
          <p className="text-sm text-grey-400 leading-relaxed">{paul.body}</p>
        </div>
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <div className="text-xs text-grey-500 mb-1 font-mono">{anton.role}</div>
          <h3 className="text-lg font-semibold text-white mb-3">{anton.name}</h3>
          <p className="text-sm text-grey-400 leading-relaxed">{anton.body}</p>
        </div>
      </div>
      <FullDivider />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        {coreBlocks.map((c) => (
          <div key={c.h} className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
            <h3 className="text-base font-semibold text-white mb-3">{c.h}</h3>
            <p className="text-sm text-grey-400 leading-relaxed">{c.b}</p>
          </div>
        ))}
      </div>
      <FullDivider />

      <div className="relative">
        <div className="absolute inset-0 dot-grid-sm dot-fade-b pointer-events-none" style={{ opacity: 0.15 }} />
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4 relative">
          <span className="block mb-3" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono', monospace" }}>{t("principlesEyebrow")}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("principlesTitle")}</h2>
        </div>
        <FullDivider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800 relative">
          {principles.map((p) => (
            <div key={p.n} className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
              <div className="font-mono text-xs mb-3" style={{ color: "#FF3C00" }}>{p.n}</div>
              <h3 className="text-base font-semibold text-white mb-2">{p.title}</h3>
              <p className="text-sm text-grey-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
      <FullDivider />

      <div className="relative bg-grey-975">
        <img
          src="/assets/team/founder-desk-wide.png"
          alt=""
          loading="lazy"
          className="w-full h-auto aspect-[21/8] object-cover object-center"
          style={{ filter: "grayscale(20%)" }}
        />
      </div>
      <FullDivider />

      <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4">
        <span className="block mb-3" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono', monospace" }}>{t("productsEyebrow")}</span>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("productsTitle")}</h2>
        <p className="text-sm text-grey-400 max-w-2xl leading-relaxed">{t("productsLead")}</p>
      </div>
      <FullDivider />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        {products.map((p) => (
          <ProductAnchor
            key={p.slug}
            slug={p.slug}
            className="bg-grey-975 group relative overflow-hidden block transition-colors hover:bg-white/[0.02]"
          >
            {IMAGES[p.slug] && (
              <div className="relative aspect-[16/9] overflow-hidden bg-grey-900">
                <img
                  src={IMAGES[p.slug]}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 group-hover:[filter:grayscale(0%)] group-hover:opacity-95"
                  style={{ filter: "grayscale(60%)", opacity: 0.7 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-grey-975 via-grey-975/30 to-transparent pointer-events-none" />
              </div>
            )}
            <div className="px-5 sm:px-8 md:px-[66px] py-8">
              <h3 className="text-base font-semibold text-white/80 group-hover:text-white transition-colors mb-1">{p.name}</h3>
              <p className="text-sm text-grey-500 leading-relaxed">{p.desc}</p>
            </div>
          </ProductAnchor>
        ))}
        <a
          href="https://github.com/0-AI-UG"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12 group transition-colors hover:bg-white/[0.02] flex flex-col justify-center"
        >
          <h3 className="text-base font-semibold text-white mb-3">{githubCard.title}</h3>
          <p className="text-sm text-grey-400 leading-relaxed mb-4">{githubCard.body}</p>
          <span className="inline-flex items-center gap-1.5 text-sm text-white/60 group-hover:text-white transition-colors">
            github.com/0-AI-UG
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </span>
        </a>
      </div>
      <FullDivider />

      <div className="relative">
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4">
          <span className="block mb-3" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono', monospace" }}>{t("stackEyebrow")}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("stackTitle")}</h2>
          <p className="text-sm text-grey-400 max-w-2xl leading-relaxed">{t("stackLead")}</p>
        </div>
        <FullDivider />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-grey-800">
          {stack.map((s) => (
            <div key={s.label} className="bg-grey-975 px-5 py-6">
              <div className="text-sm font-semibold text-white mb-1">{s.label}</div>
              <div className="text-xs text-grey-500 font-mono">{s.note}</div>
            </div>
          ))}
        </div>
      </div>
      <FullDivider />

      <div className="relative">
        <div className="absolute inset-0 dot-grid-sm dot-fade-b pointer-events-none" style={{ opacity: 0.15 }} />
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4 relative">
          <span className="block mb-3" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "'JetBrains Mono', monospace" }}>{t("servicesEyebrow")}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("servicesTitle")}</h2>
        </div>
        <FullDivider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800 border-t border-grey-800 relative">
          {services.map((s) => (
            <a
              key={s.label}
              href={s.href}
              onClick={(e) => { e.preventDefault(); navigate(s.href); }}
              className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12 group transition-colors hover:bg-white/[0.02]"
            >
              <h3 className="text-base font-semibold text-white/70 group-hover:text-white transition-colors mb-2">{s.label}</h3>
              <p className="text-sm text-grey-500 leading-relaxed">{s.desc}</p>
            </a>
          ))}
        </div>
      </div>
      <FullDivider />

      <div className="px-5 sm:px-8 md:px-[66px] py-14 md:py-20 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 max-w-2xl mx-auto leading-tight">{t("ctaTitle")}</h2>
        <p className="text-sm text-grey-400 max-w-md mx-auto leading-relaxed mb-8">{t("ctaLead")}</p>
        <a
          href="/contact"
          onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
          className="inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-2.5 rounded-full transition-colors"
          style={{ backgroundColor: "#FF3C00" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e63600")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#FF3C00")}
        >
          {tc("freeIntro")}
        </a>
      </div>
      <FullDivider />

    </SubPage>
  );
}
